import { ReviewDownloadButtons } from "./ReviewDownloadButtons";
import {
  KeyboardArrowDown as SortDescendingIcon,
  KeyboardArrowUp as SortAscendingIcon,
  UnfoldMore as UnsortedIcon,
  EditRounded as EditIcon,
  DoneRounded as DoneIcon,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Component from "components/Component";
import CalendarMonthButtons from "components/common/CalendarMonthButtons";
import DateRangeSelector from "components/common/DateRangeSelector";
import FilterBar from "components/common/FilterBar";
import FilterSelect from "components/common/FilterSelect";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import SmallLoader from "components/common/SmallLoader";
import SubmissionLink from "components/common/SubmissionLink";
import Visible from "components/common/Visible";
import * as paginationSupport from "components/common/pagination/Pagination.support";
import PaginationControls from "components/common/pagination/PaginationControls";
import DashboardReportSummary from "components/review/DashboardReportSummary";
import * as support from "components/review/ReviewSupport";
import {
  calculateDynamicDates,
  createColumns,
  FILTER_FIELDS,
  getColumnValues,
  LocalLoader,
  mkFilterFieldList,
} from "components/review/ReviewSupport";
import * as labels from "config.labels";
import dayjs from "dayjs";
import {
  DASHBOARD_CALENDAR_MONTH_BUTTONS,
  RENEWAL_FILTER_IS_VISIBLE,
  REVIEW_REPORTS_SHOW_ALL_VALUES_IN_FILTERS,
} from "flags";
import { useEffect, useState, useLayoutEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import * as reportService from "services/reportService";
import {
  useConfigQuery,
  useFeatureFlagsQuery,
} from "services/staticDataService";
import { useIsVisible, useTranslator } from "store/hooks/staticDataHooks";
import useDebounceCallback from "store/hooks/useDebounceCallback";

const SubmissionTableRow = ({ columns, submission, layer, index, editing }) => {
  return (
    <>
      <TableRow>
        {columns.map(({ Cell, key }) => (
          <Cell
            key={key}
            submission={submission}
            layer={layer}
            index={index}
            editing={editing}
          />
        ))}
        <TableCell>
          <SubmissionLink
            variant={"icon"}
            submissionId={submission.submissionId}
            path={`/pricing/layer_pricing/${index}`}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

const SubmissionsTable = ({
  columns,
  columnSort,
  setColumnSort,
  editing,
  ...props
}) => {
  const SortArrow = ({ name, columnKey }) => (
    <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
      <span>{name}</span>
      <IconButton
        size={"small"}
        onClick={() => {
          if (columnSort == null || columnSort.columnKey !== columnKey) {
            setColumnSort({
              columnKey,
              ascending: true,
            });
          } else if (columnSort.ascending) {
            setColumnSort({ ...columnSort, ascending: false });
          } else {
            setColumnSort(null);
          }
        }}
      >
        {columnSort == null || columnSort.columnKey !== columnKey ? (
          <UnsortedIcon />
        ) : columnSort.ascending ? (
          <SortAscendingIcon />
        ) : (
          <SortDescendingIcon />
        )}
      </IconButton>
    </Stack>
  );

  return (
    <TableContainer sx={{ maxHeight: props.maxHeight }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key}>
                <SortArrow name={column.title} columnKey={column.key} />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {props.submissions
            .map((submission, submissionIndex) => {
              return submission.layers.map((layer, layerIndex) => (
                <SubmissionTableRow
                  columns={columns}
                  key={`${submissionIndex}-${layerIndex}`}
                  submission={submission}
                  layer={layer}
                  index={layerIndex}
                  editing={editing}
                />
              ));
            })
            .flat()}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const IncludeNonRenewingSwitch = ({ values, radioToggle }) => {
  const translateLabel = useTranslator("uiLabels");
  const includeNonRenewing = values.includes("non_renewing");
  if (values.length === 1 && includeNonRenewing) {
    radioToggle("non_renewing", ["non_renewing"]);
    return <></>;
  }
  if (values.length === 0) return <></>;
  return (
    <FormControlLabel
      label={translateLabel(labels.REVIEW_REPORT_INCLUDE_NON_RENEWING, {
        default: "Include non renewing",
      })}
      labelPlacement={"start"}
      control={<Switch checked={includeNonRenewing} />}
      onChange={() => {
        radioToggle("non_renewing", ["non_renewing"]);
      }}
    />
  );
};

const FasterDashboardReport = ({ config }) => {
  const reportType = config?.type ?? "review_list";
  const summaryConfig = config?.summaryConfig;
  const columnConfig = config?.columnConfig;
  const {
    isVisible: renewalFilterIsVisible,
    isLoading: renewalFilterIsVisibleIsLoading,
  } = useIsVisible(RENEWAL_FILTER_IS_VISIBLE);
  const { isVisible: useCalendarMonthButtons } = useIsVisible(
    DASHBOARD_CALENDAR_MONTH_BUTTONS
  );

  const {
    data: flags,
    isLoading: isFeatureFlagsLoading,
  } = useFeatureFlagsQuery();

  const theme = useTheme();

  const showAllValuesInFilters =
    flags?.[REVIEW_REPORTS_SHOW_ALL_VALUES_IN_FILTERS] ?? false;

  const { data: dateFormat, isLoading: isDateFormatLoading } = useConfigQuery(
    "date_format"
  );
  const columns = useMemo(() => createColumns(columnConfig), [columnConfig]);

  const searchableColumns = useMemo(
    () => columns.filter((c) => c.isSearchable).map((c) => c.key),
    [columns]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultParams = useMemo(
    () => ({
      ...config?.defaultParams,
      ...Object.fromEntries(searchParams.entries()),
    }),
    [config?.defaultParams, searchParams]
  );

  const [filters, setFilters] = useState([]);
  const [editing, setEditing] = useState(false);
  const [columnSort, setColumnSort] = useState({
    columnKey: "inception",
    ascending: false,
  });
  const [height, setHeight] = useState(0);
  const [query, setQuery] = useState({});
  const [dateRange, setDateRange] = useState(
    defaultParams["date__dynamic"] ??
      (defaultParams["date__gte"] || defaultParams["date__lte"]
        ? "CUSTOM_RANGE"
        : null)
  );

  const [dateFilter, setDateFilter] = useState({
    from: defaultParams["date__gte"] ?? null,
    to: defaultParams["date__lte"] ?? null,
  });
  const [searchInput, debounceSetSearchInput] = useState(null);
  const [submissionsReady, setSubmissionsReady] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const filterDialogDefault = {
    open: false,
    context: null,
    items: [],
    sortItems: true,
    initialSelected: [],
  };
  const [filterDialog, setFilterDialog] = useState(filterDialogDefault);
  const [
    displayableSubmissionsReady,
    setDisplayableSubmissionsReady,
  ] = useState(false);
  const [displayableSubmissions, setDisplayableSubmissions] = useState([]);
  const [metricsSubmissions, setMetricsSubmissions] = useState([]);

  const { set: setSearchInput } = useDebounceCallback(debounceSetSearchInput);
  useEffect(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (dateRange !== null && dateRange !== "CUSTOM_RANGE") {
        newParams.set("date__dynamic", dateRange);
      } else {
        newParams.delete("date__dynamic");
      }

      if (dateRange === "CUSTOM_RANGE" || dateRange === "CALENDAR_MONTH") {
        if (dateFilter.from) newParams.set("date__gte", dateFilter.from);
        else newParams.delete("date__gte");

        if (dateFilter.to) newParams.set("date__lte", dateFilter.to);
        else newParams.delete("date__lte");
      } else {
        newParams.delete("date__gte");
        newParams.delete("date__lte");
      }

      return newParams;
    });
  }, [dateRange, dateFilter, setSearchParams]);

  useEffect(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      // Update filter params for all filters
      (filters ?? []).forEach((filter) => {
        if (filter.name) {
          const paramKey = `${filter.name}__in`;
          if (filter.value?.length > 0) {
            const encodedValues = filter.value
              .map((v) => encodeURIComponent(v))
              .join(",");
            newParams.set(paramKey, encodedValues);
          } else {
            newParams.delete(paramKey);
          }
        }
      });

      return newParams;
    });
  }, [filters, setSearchParams]);

  useEffect(() => {
    const yearStartMonthIndex = dateFormat?.yearStartMonthIndex ?? 0;
    if (
      dateRange !== null &&
      dateRange !== "ALL_TIME" &&
      dateRange !== "CALENDAR_MONTH" &&
      dateRange !== "CUSTOM_RANGE"
    ) {
      const { from, to } = calculateDynamicDates(
        dateRange,
        yearStartMonthIndex
      );
      if (from && to) {
        setDateFilter({
          from,
          to,
        });
      }
    } else if (dateRange === "ALL_TIME") {
      setDateFilter({
        from: null,
        to: null,
      });
    }
  }, [defaultParams, dateFormat, dateRange]);

  useEffect(() => {
    if (
      !renewalFilterIsVisibleIsLoading &&
      !isFeatureFlagsLoading &&
      !isDateFormatLoading &&
      !filters.length
    ) {
      const filters = mkFilterFieldList({ columns, defaultParams });
      const renewalFilter = {
        context: "Renewal",
        name: "Renewal",
        default: [],
        value: (defaultParams?.["Renewal__in"]?.split(",") ?? []).map(
          decodeURIComponent
        ),
        radioButtons: true,
        extra: IncludeNonRenewingSwitch,
      };
      if (renewalFilterIsVisible) {
        setFilters([renewalFilter, ...filters]);
      } else {
        setFilters(filters);
      }
    }
  }, [
    filters,
    columns,
    isFeatureFlagsLoading,
    isDateFormatLoading,
    defaultParams,
    renewalFilterIsVisible,
    renewalFilterIsVisibleIsLoading,
  ]);

  useLayoutEffect(() => {
    const updateSize = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    let args = {};
    const renewalFilter = filters.find((f) => f.context === "Renewal");
    const filtsList = filters.reduce((res, filter) => {
      if (filter.context !== "Renewal" && filter.value?.length) {
        res.push({ key: filter.name, values: filter.value });
      }
      return res;
    }, []);
    if (filtsList?.length) {
      if (showAllValuesInFilters) args["orIns"] = filtsList;
      else args["andIns"] = filtsList;
    }

    if (dateFilter.from) args["dateFrom"] = dateFilter.from;
    if (dateFilter.to) args["dateTo"] = dateFilter.to;

    if (searchInput?.trim()) {
      args["searchString"] = searchInput.trim();
      args["searchColumns"] = searchableColumns;
    }
    if (renewalFilter?.value?.length) {
      const nonRenewing = renewalFilter.value.includes("non_renewing");
      const numeric = renewalFilter.value.filter((v) => /^\d+ /.test(v));
      if (numeric.length > 0) {
        args["expiring"] = {
          maxDays: Number(numeric[0].split(" ")[0]),
          includeNonRenewing: nonRenewing,
        };
      }
    }

    args["requiredColumns"] = columns.map((e) => e.key);
    setQuery({
      type: reportType,
      args,
      columnMapping: columnConfig?.columnMapping || {},
      columnOrdering: columnConfig?.columnOrdering ?? {},
      format: "json",
      filename: reportType,
    });
  }, [
    showAllValuesInFilters,
    setQuery,
    columnConfig,
    dateFilter,
    searchInput,
    searchableColumns,
    columns,
    filters,
    reportType,
  ]);

  const {
    data: flat_submissions,
    isLoading: flat_submissionsLoading,
    isFetching: flat_submissionsFetching,
    status: flat_submissionsStatus,
    error: flat_submissionsError,
  } = reportService.useSyncJsonReportQuery(query, {
    skip: !query.type || (query?.args?.requiredColumns?.length ?? 0) === 0,
  });

  const isLoading =
    flat_submissionsLoading || isFeatureFlagsLoading || isDateFormatLoading;
  const isReloading = flat_submissionsFetching || isLoading;

  useEffect(() => {
    const dataAvailable = !isReloading;
    if (dataAvailable) {
      setSubmissions(support.structureFlattenedData(flat_submissions));
    }
    setSubmissionsReady(dataAvailable);
  }, [flat_submissions, isReloading]);

  const submissionsNotReadyOrLoading = !submissionsReady || isReloading;

  const {
    data: unfilteredSubmissions,
    isLoading: unfilteredLoading,
    status: unfilteredSubmissionsStatus,
    error: unfilteredSubmissionError,
  } = reportService.useSyncJsonReportQuery(
    {
      type: reportType,
      args: { requiredColumns: FILTER_FIELDS },
    },
    {
      skip: isFeatureFlagsLoading || !showAllValuesInFilters,
    }
  );

  const isRejected =
    unfilteredSubmissionsStatus === "rejected" ||
    flat_submissionsStatus === "rejected";
  const error =
    unfilteredSubmissionsStatus === "rejected"
      ? unfilteredSubmissionError
      : flat_submissionsStatus === "rejected"
      ? flat_submissionsError
      : "Unknown error";

  const sortedColumn =
    columns.filter((c) => c.key === columnSort?.columnKey)?.[0] ?? columns[0];

  const updateFilter = (context, value) => {
    const updated = filters.map((item) => {
      return {
        ...item,
        value: item.context === context ? value : item.value,
      };
    });
    setFilters(updated);
  };

  useEffect(() => {
    if (submissionsNotReadyOrLoading) {
      setDisplayableSubmissionsReady(false);
    } else {
      const filteredSubList = submissions.filter((s) => s.layers?.length);

      setMetricsSubmissions(filteredSubList.map((sub) => sub?.layers).flat());

      const flattened = filteredSubList.flatMap((s) =>
        sortedColumn?.splitLayersOnSort
          ? (s.layers ?? []).map((layer) => ({ ...s, layers: [layer] }))
          : [s]
      );

      if (sortedColumn?.compare) {
        flattened.sort(
          support.invertComparatorIf(!columnSort?.ascending)(
            sortedColumn.compare
          )
        );
      }
      setDisplayableSubmissions(flattened);
      setDisplayableSubmissionsReady(true);
    }
  }, [
    columnSort?.ascending,
    submissionsNotReadyOrLoading,
    sortedColumn?.compare,
    sortedColumn?.splitLayersOnSort,
    submissions,
  ]);
  const displayableSubmissionsNotReadyOrLoading =
    !displayableSubmissionsReady || submissionsNotReadyOrLoading;

  const openFilterDialog = (context) => {
    const filter = filters.filter((x) => x.context === context)[0];
    setFilterDialog({
      ...filterDialog,
      open: true,
      context: context,
      items: getColumnValues(
        filter.name,
        showAllValuesInFilters ? unfilteredSubmissions : flat_submissions
      ),
      sortItems: true,
      selected: filter.value,
      radioButtons: filter.radioButtons || false,
      extra: filter.extra,
    });
  };

  const closeFilterDialog = () => {
    setFilterDialog(filterDialogDefault);
  };

  const usePagination =
    !!columnConfig?.pagination?.enabled ||
    (displayableSubmissions?.length ?? 0) > 300;
  const pageSizeOptions = columnConfig?.pagination?.pageSizeOptions ?? [
    25,
    50,
    100,
  ];

  const {
    paginatedRows,
    rowCount,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
  } = paginationSupport.usePagination(displayableSubmissions, {
    defaultPageSize:
      columnConfig?.pagination?.defaultPageSize ?? pageSizeOptions?.[0],
  });

  const heightOffset =
    210 + (summaryConfig == null ? 0 : 100) + (usePagination ? 50 : 0);

  return (
    <>
      <Component>
        {isRejected ? (
          error
        ) : isLoading ? (
          <LocalLoader />
        ) : (
          <>
            {unfilteredLoading ? (
              <SmallLoader />
            ) : (
              <>
                <Box position={"relative"}>
                  <FilterBar
                    filters={filters}
                    onClickFilter={(context) => openFilterDialog(context)}
                    onChangeFilters={(filters) => setFilters(filters)}
                    onChangeSearchInput={(searchText) =>
                      setSearchInput(searchText)
                    }
                  />
                  <Box
                    top={0}
                    right={0}
                    sx={{
                      position: {
                        [dateRange === "CUSTOM_RANGE" ||
                        dateRange === "CALENDAR_MONTH"
                          ? "md"
                          : "sm"]: "relative",
                        [dateRange === "CUSTOM_RANGE" ||
                        dateRange === "CALENDAR_MONTH"
                          ? "lg"
                          : "md"]: "absolute",
                      },
                      paddingTop: {
                        xs: "8px",
                        [dateRange === "CUSTOM_RANGE" ||
                        dateRange === "CALENDAR_MONTH"
                          ? "lg"
                          : "md"]: "0px",
                      },
                    }}
                  >
                    <Stack
                      direction={{
                        xs: "column",
                        [dateRange === "CUSTOM_RANGE" ||
                        dateRange === "CALENDAR_MONTH"
                          ? "lg"
                          : "md"]: "row",
                      }}
                      spacing={1}
                      alignItems={"flex-start"}
                    >
                      <Stack
                        direction={"row"}
                        spacing={1}
                        alignItems={"center"}
                      >
                        <DateRangeSelector
                          selectedRange={dateRange}
                          setSelectedRange={setDateRange}
                        />
                        {dateRange === "CALENDAR_MONTH" &&
                          useCalendarMonthButtons && (
                            <CalendarMonthButtons
                              dateFilter={dateFilter}
                              onChange={(range) => setDateFilter(range)}
                            />
                          )}
                        {(dateRange === "CUSTOM_RANGE" ||
                          (dateRange === "CALENDAR_MONTH" &&
                            !useCalendarMonthButtons)) && (
                          <>
                            <FormattedDatePicker
                              label={"From"}
                              compact
                              type={
                                dateRange === "CALENDAR_MONTH"
                                  ? "month"
                                  : "date"
                              }
                              onChangeDate={(date) => {
                                let newDate = date;
                                if (dateRange === "CALENDAR_MONTH" && date) {
                                  newDate = dayjs(date)
                                    .startOf("month")
                                    .format("YYYY-MM-DD");
                                }
                                setDateFilter({
                                  ...dateFilter,
                                  from: newDate,
                                });
                              }}
                              value={dateFilter?.from ?? ""}
                            />
                            <FormattedDatePicker
                              label={"To"}
                              compact
                              type={
                                dateRange === "CALENDAR_MONTH"
                                  ? "month"
                                  : "date"
                              }
                              onChangeDate={(date) => {
                                let newDate = date;
                                if (dateRange === "CALENDAR_MONTH" && date) {
                                  newDate = dayjs(date)
                                    .endOf("month")
                                    .format("YYYY-MM-DD");
                                }
                                setDateFilter({
                                  ...dateFilter,
                                  to: newDate,
                                });
                              }}
                              value={dateFilter?.to ?? ""}
                            />
                          </>
                        )}
                      </Stack>
                      <Visible
                        byTag={"submissionStatus.editable"}
                        defaultVisible={false}
                      >
                        <Button
                          variant={editing ? "outlined" : "contained"}
                          disableElevation
                          color={"primary"}
                          onClick={(e) => {
                            setEditing(!editing);
                          }}
                          startIcon={editing ? <DoneIcon /> : <EditIcon />}
                          sx={{
                            width: "92px",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            color: editing ? theme.brand.accent[600] : null,
                          }}
                        >
                          {editing ? "Done" : "Edit"}
                        </Button>
                      </Visible>
                      <ReviewDownloadButtons
                        name={config?.title ?? "SubmissionReview"}
                        type={reportType}
                        filterArgs={query}
                      />
                    </Stack>
                  </Box>
                </Box>
                <FilterSelect
                  context={filterDialog.context}
                  items={filterDialog.items}
                  sortItems={filterDialog.sortItems}
                  initialSelected={filterDialog.selected}
                  open={filterDialog.open}
                  radioButtons={filterDialog.radioButtons}
                  Extra={filterDialog.extra}
                  onClose={closeFilterDialog}
                  onChange={updateFilter}
                />
              </>
            )}
            {displayableSubmissionsNotReadyOrLoading ? (
              <LocalLoader />
            ) : (
              <>
                {summaryConfig && (
                  <DashboardReportSummary
                    config={summaryConfig}
                    submissions={metricsSubmissions}
                    onChange={updateFilter}
                  />
                )}
                <SubmissionsTable
                  columns={columns}
                  columnSort={columnSort}
                  setColumnSort={setColumnSort}
                  submissions={
                    usePagination ? paginatedRows : displayableSubmissions
                  }
                  maxHeight={height - heightOffset}
                  editing={editing}
                />
                {usePagination && (
                  <PaginationControls
                    rowCount={rowCount}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    pageCount={pageCount}
                    sx={{ marginTop: 1 }}
                  />
                )}
              </>
            )}
          </>
        )}
      </Component>
    </>
  );
};

export default FasterDashboardReport;
